import {
  TABLE_TITLE_COLUMN_MAX_WIDTH,
  TABLE_TITLE_COLUMN_MIN_WIDTH,
  TABLE_VALUE_COLUMN_MAX_WIDTH,
  TABLE_VALUE_COLUMN_MIN_WIDTH,
} from './Table.constant'

const CELL_PADDING = 24
const CHAR_FALLBACK = 8
const BODY_FONT =
  '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
const HEADER_FONT =
  '600 14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'

let measureCanvas: HTMLCanvasElement | null = null

function measureTextWidth(text: string, font: string) {
  const value = String(text ?? '')
  if (!value) return 0

  if (typeof document === 'undefined') {
    return value.length * CHAR_FALLBACK
  }

  if (!measureCanvas) {
    measureCanvas = document.createElement('canvas')
  }
  const context = measureCanvas.getContext('2d')
  if (!context) {
    return value.length * CHAR_FALLBACK
  }

  context.font = font
  return context.measureText(value).width
}

function maxMeasuredWidth(
  values: Array<string | number | null | undefined>,
  font: string
) {
  return values.reduce<number>((max, value) => {
    const width = measureTextWidth(String(value ?? ''), font)
    return width > max ? width : max
  }, 0)
}

/** Content width clamped between minWidth and maxWidth. */
export function resolveColumnWidth(
  title: string,
  cellValues: Array<string | number | null | undefined>,
  minWidth: number,
  maxWidth: number
) {
  const cellWidth = maxMeasuredWidth(cellValues, BODY_FONT)
  const headerWidth = measureTextWidth(title, HEADER_FONT)
  const contentWidth = Math.max(cellWidth, headerWidth) + CELL_PADDING

  return Math.min(maxWidth, Math.max(minWidth, Math.ceil(contentWidth)))
}

export function getTitleColumnBounds() {
  return {
    minWidth: TABLE_TITLE_COLUMN_MIN_WIDTH,
    maxWidth: TABLE_TITLE_COLUMN_MAX_WIDTH,
  }
}

export function getValueColumnBounds() {
  return {
    minWidth: TABLE_VALUE_COLUMN_MIN_WIDTH,
    maxWidth: TABLE_VALUE_COLUMN_MAX_WIDTH,
  }
}

export function resolveTitleColumnWidth(
  title: string,
  cellValues: Array<string | number | null | undefined>
) {
  return resolveColumnWidth(
    title,
    cellValues,
    TABLE_TITLE_COLUMN_MIN_WIDTH,
    TABLE_TITLE_COLUMN_MAX_WIDTH
  )
}

export function resolveValueColumnWidth(
  title: string,
  cellValues: Array<string | number | null | undefined>
) {
  return resolveColumnWidth(
    title,
    cellValues,
    TABLE_VALUE_COLUMN_MIN_WIDTH,
    TABLE_VALUE_COLUMN_MAX_WIDTH
  )
}

export function sumColumnWidths(
  columns: Array<{ width?: number | string }>
) {
  return columns.reduce<number>((total, column) => {
    const width = Number(column.width)
    return total + (Number.isFinite(width) ? width : 0)
  }, 0)
}
